import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User } from "lucide-react";

export default function Signup() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/app/planner" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await register(name, email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Your account is ready. Welcome!");
      nav("/app/planner");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: "var(--bg-primary)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6" data-testid="brand-link">
          <div className="w-10 h-10 rounded-xl bg-amber-400 flex items-center justify-center text-white font-bold">W</div>
          <span className="font-heading font-bold text-2xl text-slate-800">My Week</span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <h1 className="font-heading font-bold text-2xl text-slate-800 mb-1">Sign up</h1>
          <p className="text-sm text-slate-600 mb-6">Make an account. It takes about a minute.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-slate-700 font-medium">Your name</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  id="name" type="text" required minLength={1}
                  data-testid="signup-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="pl-9 h-11 rounded-xl"
                  placeholder="Alex"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  id="email" type="email" required
                  data-testid="signup-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 rounded-xl"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  id="password" type="password" required minLength={6}
                  data-testid="signup-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11 rounded-xl"
                  placeholder="At least 6 letters"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">Use six or more letters or numbers.</p>
            </div>
            <Button
              type="submit" disabled={submitting}
              data-testid="signup-submit-btn"
              className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
            >
              {submitting ? "Creating account..." : "Create my account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-amber-700 font-semibold hover:underline" data-testid="switch-login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
